import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { AEO_CONTENT, LAST_UPDATED } from "@/data/aeoContent";
import { organizationSchema, breadcrumbSchema } from "@/lib/seo-schemas";

const AllLinks = ({ excludeSlug, excludeType }) => {
  const items = [
    ...AEO_CONTENT.PILLAR_PAGES.map((p) => ({ ...p, type: "guide", path: `/guide/${p.slug}` })),
    ...AEO_CONTENT.COMPARISON_PAGES.map((p) => ({ ...p, type: "compare", path: `/compare/${p.slug}` })),
    ...AEO_CONTENT.LOCATION_PAGES.map((p) => ({ ...p, type: "location", path: `/location/${p.slug}` })),
    { slug: "faq", type: "faq", path: "/faq", title: "Frequently Asked Questions" },
  ].filter((p) => !(p.slug === excludeSlug && p.type === excludeType));

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map((p) => (
        <Link key={p.path} to={p.path} className="shg-card p-5 group block" data-testid={`related-${p.type}-${p.slug}`}>
          <div className="font-mono text-[10px] tracking-[0.2em] text-[var(--text-muted)] mb-2 uppercase">{p.type}</div>
          <div className="font-display text-lg group-hover:text-[var(--red)] transition-colors flex items-center justify-between gap-3">
            <span>{p.title}</span>
            <ArrowUpRight size={16} className="text-[var(--red)]" />
          </div>
        </Link>
      ))}
    </div>
  );
};

export const AeoPageShell = ({ eyebrow, title, summary, path, jsonLd, excludeSlug, excludeType, children }) => {
  useSEO({
    title: `${title} | Stagehook Global`,
    description: summary,
    keywords: `${title.toLowerCase()}, exhibition stall designer, expo booth builder, Stagehook Global`,
    path,
    jsonLd,
  });

  return (
    <main className="pt-[100px] pb-24" data-testid="aeo-page">
      <div className="shg-container">
        <div className="max-w-3xl mb-14">
          <div className="shg-eyebrow mb-5">{eyebrow}</div>
          <h1 className="font-display text-5xl md:text-6xl leading-[0.95] mb-6">{title}</h1>
          <p className="text-lg text-[var(--text-soft)] leading-relaxed">{summary}</p>
          <div className="font-mono text-[10px] tracking-[0.2em] text-[var(--text-muted)] mt-6">
            LAST UPDATED · {LAST_UPDATED}
          </div>
        </div>

        <article className="prose-shg max-w-4xl">{children}</article>

        <section className="mt-20 pt-14 border-t" style={{ borderColor: "var(--line)" }}>
          <h2 className="font-display text-3xl mb-8">Related resources</h2>
          <AllLinks excludeSlug={excludeSlug} excludeType={excludeType} />
        </section>

        <div className="mt-14 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row md:items-center justify-between gap-6" style={{ background: "var(--surface)", border: "1px solid var(--line-strong)" }}>
          <div>
            <div className="shg-eyebrow mb-3">Ready to exhibit?</div>
            <h3 className="font-display text-2xl md:text-3xl">Get concept renders in 72 hours.</h3>
          </div>
          <Link to="/contact" className="btn-primary self-start">Get a Quote <ArrowUpRight size={16} /></Link>
        </div>
      </div>
    </main>
  );
};

export const Bullet = ({ children }) => (
  <li className="flex gap-3 items-start mb-3">
    <Check size={18} className="text-[var(--red)] mt-1 flex-shrink-0" />
    <span className="text-[var(--text-soft)]">{children}</span>
  </li>
);

export const H2 = ({ children }) => (
  <h2 className="font-display text-3xl md:text-4xl mt-14 mb-5">{children}</h2>
);

export const P = ({ children }) => (
  <p className="text-[var(--text-soft)] text-[16px] leading-relaxed mb-4">{children}</p>
);
